import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import Loader from '@/common/components/Loader'
import { Icon } from '@/common/components/Icon'
import { Header, PageTitle } from '@/components'
import { Button, Card } from '@/components/ui'
import SelectField from '@/components/select-field'
import { IColumn, Table } from '@/core/components/Table'
import { useCheckPermission } from '@/core/hooks/use-check-permission'
import {
  PartnerDto,
  RewardPoolDto,
  RewardPoolUpsertDto,
  RoadmapDto,
} from '@/common/models/gamification'
import {
  createRewardPool,
  deleteRewardPool,
  getPartners,
  getRewardPools,
  getRoadmaps,
  updateRewardPool,
} from '@/core/services/gamification'

import { MessageBox } from '../messages/MessageBox'
import { translationTitle } from './shared/labels'
import { CreateRewardPoolModal } from './CreateRewardPoolModal'

export const RewardPoolsPage: React.FC = () => {
  const { editionId } = useParams()
  const { hasEditPermission } = useCheckPermission({ page: 'GAMIFICATION' })

  const [pools, setPools] = useState<RewardPoolDto[]>([])
  const [partners, setPartners] = useState<PartnerDto[]>([])
  const [roadmaps, setRoadmaps] = useState<RoadmapDto[]>([])
  const [loading, setLoading] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<RewardPoolDto | null>(null)

  const [filterPartnerId, setFilterPartnerId] = useState('')
  const [filterRoadmapId, setFilterRoadmapId] = useState('')

  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>()
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')

  const handleError = (e: unknown) => {
    setIsError(true)
    setShowMessageBox(true)
    setMessage(e instanceof Error ? e.message : 'Something went wrong')
    setErrorStatus((e as { response?: { status?: number } })?.response?.status)
  }

  const notify = (msg: string) => {
    setIsError(false)
    setMessage(msg)
    setShowMessageBox(true)
  }

  const getData = useCallback(async () => {
    if (!editionId) return
    try {
      setLoading(true)
      const [{ data: poolsData }, { data: partnersData }, { data: roadmapsData }] =
        await Promise.all([
          getRewardPools({
            partnerId: filterPartnerId || undefined,
            roadmapId: filterRoadmapId || undefined,
          }),
          getPartners(editionId),
          getRoadmaps(editionId),
        ])
      setPools(poolsData ?? [])
      setPartners(partnersData ?? [])
      setRoadmaps(roadmapsData ?? [])
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }, [editionId, filterPartnerId, filterRoadmapId])

  useEffect(() => {
    getData()
  }, [getData])

  const openCreate = () => {
    setEditing(null)
    setCreateOpen(true)
  }

  const openEdit = (row: RewardPoolDto) => {
    if (!hasEditPermission) return
    setEditing(row)
    setCreateOpen(true)
  }

  const handleSubmit = async (values: RewardPoolUpsertDto, id?: string) => {
    try {
      setLoading(true)
      if (id) {
        await updateRewardPool(id, values)
        notify('Reward pool updated successfully')
      } else {
        await createRewardPool(values)
        notify('Reward pool created successfully')
      }
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      setLoading(true)
      await deleteRewardPool(id)
      notify('Reward pool deleted successfully')
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const partnerName = useCallback(
    (partnerId?: string | null) =>
      partners.find((p) => p.id === partnerId)?.name ?? '—',
    [partners],
  )

  const roadmapName = useCallback(
    (roadmapId?: string | null) => {
      const roadmap = roadmaps.find((r) => r.id === roadmapId)
      if (!roadmap) return '—'
      return translationTitle(roadmap.translations, `${roadmap.scope} roadmap`)
    },
    [roadmaps],
  )

  const partnerFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      ...partners.map((p) => ({ value: p.id, label: p.name })),
    ],
    [partners],
  )

  const roadmapFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      ...roadmaps.map((r) => ({
        value: r.id,
        label: translationTitle(r.translations, `${r.scope} roadmap`),
      })),
    ],
    [roadmaps],
  )

  const columns: IColumn[] = useMemo(
    () => [
      {
        title: 'Partner',
        accessor: 'partnerId',
        render: (row: RewardPoolDto) => (
          <span className="text-monochrome-2">{partnerName(row.partnerId)}</span>
        ),
      },
      {
        title: 'Roadmap',
        accessor: 'roadmapId',
        render: (row: RewardPoolDto) => (
          <span className="text-monochrome-2">{roadmapName(row.roadmapId)}</span>
        ),
      },
      {
        title: 'Total Qty',
        accessor: 'totalQty',
        render: (row: RewardPoolDto) => (
          <span className="text-monochrome-1">
            {row.depletionPolicy === 'UNLIMITED' || row.totalQty == null
              ? '∞'
              : row.totalQty}
          </span>
        ),
      },
      {
        title: 'Consumed',
        accessor: 'consumed',
        render: (row: RewardPoolDto) => (
          <span className="text-monochrome-1">{row.consumed}</span>
        ),
      },
      {
        title: 'Policy',
        accessor: 'depletionPolicy',
        render: (row: RewardPoolDto) => (
          <span className="text-monochrome-2">{row.depletionPolicy}</span>
        ),
      },
      {
        title: 'Edit',
        accessor: '',
        render: (row: RewardPoolDto) => (
          <a
            className="view text-accent-1 underline"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              openEdit(row)
            }}
          >
            Edit
          </a>
        ),
      },
    ],
    [hasEditPermission, partnerName, roadmapName],
  )

  return (
    <>
      <Header
        pageTitle="Gamification"
        actions={
          hasEditPermission && (
            <Button variant="primary-bordered" onClick={openCreate}>
              Add Pool
              <Icon icon="plus-circle" width="18" height="18" viewBox="0 0 18 18" />
            </Button>
          )
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle icon="gift" title="Reward Pools" className="mb-5" />

        <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField
            label="Partner"
            placeholder="All"
            options={partnerFilterOptions}
            value={filterPartnerId || 'all'}
            onValueChange={(v) => setFilterPartnerId(v === 'all' ? '' : v)}
          />
          <SelectField
            label="Roadmap"
            placeholder="All"
            options={roadmapFilterOptions}
            value={filterRoadmapId || 'all'}
            onValueChange={(v) => setFilterRoadmapId(v === 'all' ? '' : v)}
          />
        </div>

        <Table
          className="[&_th]:!whitespace-nowrap"
          columns={columns}
          data={pools}
          noDataMessage="No reward pools yet"
        />
      </Card>

      <CreateRewardPoolModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        pool={editing}
        partners={partners}
        roadmaps={roadmaps}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
      />

      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
